import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { FileEdit, Upload, Undo2, AlertTriangle, Loader2, Calendar, Clock } from 'lucide-react';
import { apiClient } from '@/services/api';
import type { SyncDraftArea } from '@/types/api';
import { useHasPermission } from '@/hooks/usePermission';
import { useRestaurant } from '@/hooks/useRestaurant';
import { resetPendingSyncPolling } from '@/utils/pendingSyncPolling';
import { zonedWallTimeToUtcIso, utcIsoToZonedParts, formatInstantInZone } from '@/utils/restaurantTime';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui';

interface DraftModeBarProps {
  restaurantId: number;
  area: SyncDraftArea;
  /** Permission required to stage, publish or discard changes in this area. */
  permission: string;
}

type DialogKind = null | 'exit' | 'push' | 'cancel' | 'schedule';

/**
 * Draft mode control for one dashboard area.
 *
 * <p>While draft mode is on, edits made on this page are staged instead of
 * being sent to the POS terminals. The operator then publishes the batch or
 * discards it.
 *
 * <p>The amber indicator is itself the way to leave draft mode: turning it off
 * means deciding what happens to the staged work, so clicking it opens a
 * three-way prompt (push / discard / stay in draft). Push and Cancel each act
 * immediately on their own intent, so they only need a plain confirmation.
 */
export function DraftModeBar({
  restaurantId,
  area,
  permission,
}: DraftModeBarProps) {
  const queryClient = useQueryClient();
  const canEdit = useHasPermission(permission);

  const { data: restaurant } = useRestaurant(restaurantId);
  const timezone = restaurant?.timezone ?? 'UTC';

  const [dialog, setDialog] = useState<DialogKind>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  const { data: status, isLoading } = useQuery({
    queryKey: ['draftStatus', restaurantId, area],
    queryFn: () => apiClient.getDraftStatus(restaurantId, area),
    // Keep the staged count live: it changes from other pages saving with draft
    // mode on, from a colleague enabling it, and from this area's own edits.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (query.state.data?.enabled ? 5000 : false),
  });

  const refreshAfterAction = useCallback(async () => {
    // Push/cancel rewrite or release a whole area's worth of rows across
    // several pages' queries — invalidate everything rather than chase every
    // query key that might be showing one of them.
    await queryClient.invalidateQueries();
    // Rows that were pending for the entire draft session have likely used up
    // their poll budget; restart it so their new status is picked up.
    resetPendingSyncPolling();
  }, [queryClient]);

  // The 5s status poll (refetchInterval above) also picks up a push the
  // scheduler job fired automatically, server-side, with nobody clicking
  // anything here. Detect that specific transition -- enabled flips from
  // true to false without a local mutation having just caused it -- and run
  // the same broader refresh a manual push/cancel already gets, so other
  // on-page pending-sync badges (which may have already exhausted their own
  // poll budget by the time a scheduled push fires) get nudged too.
  //
  // This also fires redundantly right after a manual push/cancel's own
  // onSuccess already called refreshAfterAction once (that transition looks
  // identical from here). That extra invalidateQueries()/resetPendingSyncPolling()
  // call is harmless, so no attempt is made to distinguish the two cases.
  const previousEnabledRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (previousEnabledRef.current === true && status?.enabled === false) {
      refreshAfterAction();
    }
    previousEnabledRef.current = status?.enabled;
  }, [status?.enabled, refreshAfterAction]);

  const enableMutation = useMutation({
    mutationFn: () => apiClient.enableDraftMode(restaurantId, area),
    onSuccess: refreshAfterAction,
    onError: () => setActionError('Could not turn on draft mode. Please try again.'),
  });

  const pushMutation = useMutation({
    mutationFn: () => apiClient.pushDraftChanges(restaurantId, area),
    onSuccess: async () => {
      setDialog(null);
      await refreshAfterAction();
    },
    onError: () => setActionError('Could not publish the staged changes. They are still staged.'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiClient.cancelDraftChanges(restaurantId, area),
    onSuccess: async () => {
      setDialog(null);
      await refreshAfterAction();
    },
    onError: () => setActionError('Could not discard the staged changes. Nothing was reverted.'),
  });

  const scheduleMutation = useMutation({
    mutationFn: (pushAt: string) => apiClient.scheduleDraftPush(restaurantId, area, pushAt),
    onSuccess: () => {
      setDialog(null);
      queryClient.invalidateQueries({ queryKey: ['draftStatus', restaurantId, area] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Could not schedule the push. Please try again.';
      setActionError(message);
    },
  });

  const clearScheduleMutation = useMutation({
    mutationFn: () => apiClient.clearDraftSchedule(restaurantId, area),
    onSuccess: () => {
      setDialog(null);
      queryClient.invalidateQueries({ queryKey: ['draftStatus', restaurantId, area] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Could not remove the schedule. Please try again.';
      setActionError(message);
    },
  });

  const isBusy = enableMutation.isPending || pushMutation.isPending || cancelMutation.isPending
    || scheduleMutation.isPending || clearScheduleMutation.isPending;

  const openDialog = (kind: DialogKind) => {
    setActionError(null);
    if (kind === 'schedule') {
      if (status?.scheduledPushAt) {
        const parts = utcIsoToZonedParts(status.scheduledPushAt, timezone);
        setScheduleDate(parts.date);
        setScheduleTime(parts.time);
      } else {
        setScheduleDate('');
        setScheduleTime('');
      }
    }
    setDialog(kind);
  };

  const closeDialog = () => {
    if (isBusy) return;
    setDialog(null);
  };

  if (isLoading || !status || !canEdit) {
    return null;
  }

  const stagedCount = status.stagedChangeCount;

  // Off: a single control to start staging.
  if (!status.enabled) {
    return (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => {
            setActionError(null);
            enableMutation.mutate();
          }}
          disabled={isBusy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          title="Stage changes on this page instead of sending them to the POS immediately"
        >
          {enableMutation.isPending
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <FileEdit className="w-4 h-4" />}
          Draft mode
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => openDialog('exit')}
          disabled={isBusy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-300 rounded-md hover:bg-amber-100 disabled:opacity-50"
          title="Turn off draft mode — decide what happens to the staged changes"
        >
          <FileEdit className="w-4 h-4" />
          Draft mode
          <span className="px-1.5 py-0.5 text-xs font-semibold text-amber-900 bg-amber-200 rounded">
            {stagedCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => openDialog('push')}
          disabled={isBusy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          <Upload className="w-4 h-4" />
          Push changes
        </button>

        <button
          type="button"
          onClick={() => openDialog('cancel')}
          disabled={isBusy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <Undo2 className="w-4 h-4" />
          Cancel changes
        </button>

        <button
          type="button"
          onClick={() => openDialog('schedule')}
          disabled={isBusy || !restaurant}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          title={!restaurant ? 'Loading restaurant details…' : undefined}
        >
          <Calendar className="w-4 h-4" />
          Schedule push
        </button>
      </div>

      <DraftBanner
        stagedCount={stagedCount}
        enabledBy={status.enabledBy}
        enabledAt={status.enabledAt}
        error={dialog === null ? actionError : null}
      />

      {status.scheduledPushAt && (
        <div className="w-full mt-3 px-4 py-3 bg-blue-50 border border-blue-300 rounded-md">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900">
              All staged changes ({stagedCount}) will be pushed automatically on{' '}
              <span className="font-medium">{formatInstantInZone(status.scheduledPushAt, timezone)}</span>
              {' '}({timezone})
              {status.scheduledBy && ` — scheduled by ${status.scheduledBy}`}
            </p>
          </div>
        </div>
      )}

      <Modal isOpen={dialog === 'exit'} onClose={closeDialog} size="md">
        <ModalHeader onClose={closeDialog}>
          {stagedCount === 1 ? '1 staged change' : `${stagedCount} staged changes`}
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-700">
            What would you like to do with the changes staged in this area?
          </p>
          <ul className="mt-4 space-y-3 text-sm text-gray-600">
            <li>
              <span className="font-medium text-gray-900">Push changes</span> — send them to the
              POS terminals now and turn draft mode off.
            </li>
            <li>
              <span className="font-medium text-gray-900">Cancel changes</span> — discard them and
              restore this area to what the POS last reported. This cannot be undone.
            </li>
            <li>
              <span className="font-medium text-gray-900">Keep draft mode on</span> — leave
              everything staged and carry on editing.
            </li>
          </ul>
          {status.scheduledPushAt && (
            <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              These changes are scheduled to be pushed automatically on{' '}
              {formatInstantInZone(status.scheduledPushAt, timezone)}. Continuing will remove this schedule.
            </p>
          )}
          {actionError && (
            <p className="mt-4 text-sm text-red-600">{actionError}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            onClick={closeDialog}
            disabled={isBusy}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Keep draft mode on
          </button>
          <button
            type="button"
            onClick={() => cancelMutation.mutate()}
            disabled={isBusy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
          >
            {cancelMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Cancel changes
          </button>
          <button
            type="button"
            onClick={() => pushMutation.mutate()}
            disabled={isBusy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {pushMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Push changes
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={dialog === 'push'} onClose={closeDialog} size="sm">
        <ModalHeader onClose={closeDialog}>Push changes?</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-700">
            Are you sure you want to send {stagedCount === 1 ? 'the 1 staged change' : `all ${stagedCount} staged changes`} to
            the POS terminals now? Draft mode will be turned off.
          </p>
          {status.scheduledPushAt && (
            <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              These changes are scheduled to be pushed automatically on{' '}
              {formatInstantInZone(status.scheduledPushAt, timezone)}. Continuing will remove this schedule.
            </p>
          )}
          {actionError && (
            <p className="mt-4 text-sm text-red-600">{actionError}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            onClick={closeDialog}
            disabled={isBusy}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => pushMutation.mutate()}
            disabled={isBusy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {pushMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            OK
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={dialog === 'cancel'} onClose={closeDialog} size="sm">
        <ModalHeader onClose={closeDialog}>Cancel changes?</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-700">
            Are you sure you want to discard {stagedCount === 1 ? 'the 1 staged change' : `all ${stagedCount} staged changes`} and
            restore this area to what the POS last reported? This cannot be undone.
          </p>
          {status.scheduledPushAt && (
            <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              These changes are scheduled to be pushed automatically on{' '}
              {formatInstantInZone(status.scheduledPushAt, timezone)}. Continuing will remove this schedule.
            </p>
          )}
          {actionError && (
            <p className="mt-4 text-sm text-red-600">{actionError}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            onClick={closeDialog}
            disabled={isBusy}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => cancelMutation.mutate()}
            disabled={isBusy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
          >
            {cancelMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            OK
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={dialog === 'schedule'} onClose={closeDialog} size="sm">
        <ModalHeader onClose={closeDialog}>Schedule automatic push</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-700 mb-4">
            Staged changes will be pushed automatically at this time ({timezone}).
          </p>
          <div className="space-y-3">
            <div>
              <label htmlFor="schedule-date" className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                id="schedule-date"
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label htmlFor="schedule-time" className="block text-sm font-medium text-gray-700 mb-1">
                Time
              </label>
              <input
                id="schedule-time"
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="input w-full"
              />
            </div>
          </div>
          {actionError && <p className="mt-4 text-sm text-red-600">{actionError}</p>}
        </ModalBody>
        <ModalFooter>
          {status.scheduledPushAt && (
            <button
              type="button"
              onClick={() => clearScheduleMutation.mutate()}
              disabled={isBusy}
              className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
            >
              Remove schedule
            </button>
          )}
          <button
            type="button"
            onClick={closeDialog}
            disabled={isBusy}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (!scheduleDate || !scheduleTime) {
                setActionError('Pick both a date and a time.');
                return;
              }
              scheduleMutation.mutate(zonedWallTimeToUtcIso(scheduleDate, scheduleTime, timezone));
            }}
            disabled={isBusy || !restaurant}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {scheduleMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Schedule
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

interface DraftBannerProps {
  stagedCount: number;
  enabledBy: string | null;
  enabledAt: string | null;
  error: string | null;
}

/**
 * Explains the staged state to whoever is looking at the page -- including a
 * second user who did not enable draft mode themselves and would otherwise have
 * no way to tell why their colleague's edits are not on the terminals.
 */
function DraftBanner({ stagedCount, enabledBy, enabledAt, error }: DraftBannerProps) {
  const since = enabledAt ? new Date(enabledAt).toLocaleString() : null;

  return (
    <div className="w-full mt-3 px-4 py-3 bg-amber-50 border border-amber-300 rounded-md">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">
            Draft mode is on — changes on this page are not being sent to the POS.
          </p>
          <p className="mt-1 text-amber-800">
            {stagedCount === 0
              ? 'Nothing staged yet. '
              : `${stagedCount} ${stagedCount === 1 ? 'change is' : 'changes are'} waiting to be published. `}
            {enabledBy && `Enabled by ${enabledBy}`}
            {enabledBy && since && ` on ${since}`}
            {enabledBy && '.'}
          </p>
          {error && <p className="mt-2 text-red-700">{error}</p>}
        </div>
      </div>
    </div>
  );
}

export default DraftModeBar;
